import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import styles from './FilterMonthYearPicker.module.scss';

const YEAR_MIN = 1900;
const YEAR_MAX = 2099;
/** After 2 digits, wait briefly so typing "2026" isn't cut off at "20"; then auto-fetch like 4-digit. */
const TWO_DIGIT_YEAR_COMMIT_MS = 350;

const buildUtcYearStart = (year) => new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0));

const buildUtcMonthStart = (year, monthIndex) =>
  new Date(Date.UTC(year, monthIndex, 1, 0, 0, 0, 0));

const toUtcDate = (value) => {
  if (!value) return new Date();
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const digitsOnly = (value, maxLength) => value.replace(/\D/g, '').slice(0, maxLength);

/** Map "26" → 2026 (prefer 20xx, then 19xx) within YEAR_MIN..YEAR_MAX. */
const expandTwoDigitYear = (raw) => {
  if (!/^\d{2}$/.test(raw)) return null;
  const yy = Number(raw);
  for (const century of [2000, 1900]) {
    const expanded = century + yy;
    if (expanded >= YEAR_MIN && expanded <= YEAR_MAX) return expanded;
  }
  return null;
};

const parseYear = (raw) => {
  if (/^\d{4}$/.test(raw)) {
    const nextYear = Number(raw);
    if (nextYear < YEAR_MIN || nextYear > YEAR_MAX) return null;
    return nextYear;
  }
  // Short form: "26" → 2026. Applied on blur/Enter so typing "2026" is not cut off at "20".
  return expandTwoDigitYear(raw);
};

const parseMonth = (raw) => {
  if (!raw) return null;
  const nextMonth = Number(raw);
  if (!Number.isInteger(nextMonth) || nextMonth < 1 || nextMonth > 12) return null;
  return nextMonth;
};

const FilterMonthYearPicker = ({
  year,
  month,
  onYearChange,
  onMonthChange,
  labelYear,
  labelMonth,
  direction = 'rtl',
  yearOnly = false,
}) => {
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const fieldRefs = useRef({ year: null, month: null });
  const justFocusedRef = useRef(false);
  const scrolledToSelectedRef = useRef(false);
  const yearCommitTimerRef = useRef(null);
  const [openField, setOpenField] = useState(null);
  const [focusedField, setFocusedField] = useState(null);
  const [menuStyle, setMenuStyle] = useState(null);

  const yearDate = toUtcDate(year);
  const monthDate = toUtcDate(month ?? year);
  const selectedYear = yearDate.getUTCFullYear();
  const selectedMonth = monthDate.getUTCMonth() + 1;

  const [inputYearValue, setInputYearValue] = useState(String(selectedYear));
  const [inputMonthValue, setInputMonthValue] = useState(String(selectedMonth).padStart(2, '0'));
  // Keep latest typed values for blur/Enter commits (avoids stale state when leaving the field).
  const inputYearValueRef = useRef(inputYearValue);
  const inputMonthValueRef = useRef(inputMonthValue);
  inputYearValueRef.current = inputYearValue;
  inputMonthValueRef.current = inputMonthValue;

  const clearYearCommitTimer = () => {
    if (yearCommitTimerRef.current == null) return;
    clearTimeout(yearCommitTimerRef.current);
    yearCommitTimerRef.current = null;
  };

  useEffect(() => () => clearYearCommitTimer(), []);

  const yearOptions = useMemo(() => {
    const start = YEAR_MIN;
    const end = YEAR_MAX;
    return Array.from({ length: end - start + 1 }, (_, index) => {
      const value = start + index;
      return { value, label: String(value) };
    }).reverse(); // show newest first
  }, []);

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => ({
        value: index + 1,
        label: String(index + 1).padStart(2, '0'),
      })),
    [],
  );

  useEffect(() => {
    setInputYearValue(String(selectedYear));
  }, [selectedYear]);

  useEffect(() => {
    setInputMonthValue(String(selectedMonth).padStart(2, '0'));
  }, [selectedMonth]);

  const updateMenuPosition = () => {
    const el = fieldRefs.current[openField];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportPad = 8;
    const maxHeight = 220;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPad;
    const spaceAbove = rect.top - viewportPad;
    const openUp = spaceBelow < 140 && spaceAbove > spaceBelow;
    const height = Math.min(maxHeight, openUp ? spaceAbove : spaceBelow);

    setMenuStyle({
      position: 'fixed',
      top: openUp ? undefined : rect.bottom + 6,
      bottom: openUp ? window.innerHeight - rect.top + 6 : undefined,
      left: rect.left,
      width: rect.width,
      maxHeight: Math.max(height, 96),
      zIndex: 1400,
    });
  };

  useLayoutEffect(() => {
    scrolledToSelectedRef.current = false;
    if (!openField) {
      setMenuStyle(null);
      return undefined;
    }
    updateMenuPosition();
    // Scrolling the list itself must not reposition it: each reposition sets a new
    // style object, and the re-render would re-run the option refs below.
    const onReposition = (event) => {
      if (event?.target && listRef.current?.contains(event.target)) return;
      updateMenuPosition();
    };
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [openField]);

  useEffect(() => {
    if (!openField) return undefined;

    const handlePointerDown = (event) => {
      const inList = listRef.current?.contains(event.target);
      const onToggle = event.target.closest('[data-picker-toggle="true"]');
      // Dropdown stays open only while interacting with the list or toggle icons.
      if (!inList && !onToggle) setOpenField(null);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpenField(null);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openField]);

  const applyYear = (raw, { revertIfInvalid = false } = {}) => {
    clearYearCommitTimer();
    const nextYear = parseYear(raw);
    if (nextYear == null) {
      if (revertIfInvalid) setInputYearValue(String(selectedYear));
      return;
    }
    setInputYearValue(String(nextYear));
    inputYearValueRef.current = String(nextYear);
    if (nextYear !== selectedYear) {
      onYearChange?.(buildUtcYearStart(nextYear));
    }
  };

  const applyMonth = (raw, { revertIfInvalid = false } = {}) => {
    const nextMonth = parseMonth(raw);
    if (nextMonth == null) {
      if (revertIfInvalid) setInputMonthValue(String(selectedMonth).padStart(2, '0'));
      return;
    }
    setInputMonthValue(String(nextMonth).padStart(2, '0'));
    if (nextMonth !== selectedMonth) {
      // Prefer the currently typed year (inputYearValue) to avoid race conditions
      // when the parent hasn't re-rendered after an auto-committed year.
      const yearForMonth = parseYear(inputYearValueRef.current) ?? selectedYear;
      onMonthChange?.(buildUtcMonthStart(yearForMonth, nextMonth - 1));
    }
  };

  const handleYearInputChange = (raw) => {
    clearYearCommitTimer();
    setInputYearValue(raw);
    inputYearValueRef.current = raw;

    // 4 digits: commit immediately (same as before).
    if (raw.length === 4) {
      const nextYear = parseYear(raw);
      if (nextYear != null && nextYear !== selectedYear) {
        applyYear(raw);
      }
      return;
    }

    // 2 digits: auto-commit shortly after typing stops, so "26" → 2026 fetches
    // without Enter/click, while still allowing continuous typing of "2026".
    if (raw.length === 2) {
      yearCommitTimerRef.current = setTimeout(() => {
        yearCommitTimerRef.current = null;
        if (inputYearValueRef.current !== raw) return;
        applyYear(raw);
      }, TWO_DIGIT_YEAR_COMMIT_MS);
    }
  };

  const handleMonthInputChange = (raw) => {
    setInputMonthValue(raw);

    // Auto-commit once the typed value can't grow into a different month:
    // two digits are final, and 2–9 can only be that month ("1" may still become 10/11/12).
    const isFinal = raw.length === 2 || (raw.length === 1 && raw !== '0' && raw !== '1');
    if (isFinal) {
      const nextMonth = parseMonth(raw);
      if (nextMonth != null && nextMonth !== selectedMonth) {
        applyMonth(raw);
      }
    }
  };

  // The dropdown toggles keep focus (mousedown is prevented), so the input never blurs.
  // Commit whatever is typed first, otherwise the pending value is silently dropped.
  const commitField = (field) => {
    if (field === 'year') applyYear(inputYearValueRef.current, { revertIfInvalid: true });
    if (field === 'month') applyMonth(inputMonthValueRef.current, { revertIfInvalid: true });
  };

  const toggleField = (field) => {
    if (focusedField) commitField(focusedField);
    setOpenField((current) => (current === field ? null : field));
  };

  const handleYearSelect = (nextYear) => {
    setInputYearValue(String(nextYear));
    onYearChange?.(buildUtcYearStart(nextYear));
    setOpenField(null);
  };

  const handleMonthSelect = (nextMonth) => {
    setInputMonthValue(String(nextMonth).padStart(2, '0'));
    onMonthChange?.(buildUtcMonthStart(selectedYear, nextMonth - 1));
    setOpenField(null);
  };

  const openOptions =
    openField === 'year' ? yearOptions : openField === 'month' ? monthOptions : null;
  const openSelectedValue = openField === 'year' ? selectedYear : selectedMonth;
  const openSelectHandler = openField === 'year' ? handleYearSelect : handleMonthSelect;

  const list =
    openField && menuStyle && openOptions
      ? createPortal(
          <div
            ref={listRef}
            className={styles.listPortal}
            role="listbox"
            style={menuStyle}
          >
            {openOptions.map((option) => {
              const selected = option.value === openSelectedValue;
              return (
                <button
                  type="button"
                  key={String(option.value)}
                  role="option"
                  aria-selected={selected}
                  className={`${styles.option} ${selected ? styles.optionSelected : ''}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => openSelectHandler(option.value)}
                  ref={(node) => {
                    // Only center the selection when the list opens. This ref re-runs on
                    // every render, and re-centering would fight the user's scrolling.
                    if (selected && node && !scrolledToSelectedRef.current) {
                      scrolledToSelectedRef.current = true;
                      node.scrollIntoView({ block: 'center', inline: 'nearest' });
                    }
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>,
          document.body
        )
      : null;

  const renderField = ({
    field,
    label,
    inputValue,
    onInputChange,
    onCommit,
    maxLength,
    placeholder,
  }) => {
    const isOpen = openField === field;
    const isFocused = focusedField === field;

    return (
      <div
        ref={(node) => {
          fieldRefs.current[field] = node;
        }}
        className={`${styles.field} ${isOpen ? styles.fieldOpen : ''} ${isFocused ? styles.fieldFocused : ''}`}
      >
        {label ? <span className={styles.notchLabel}>{label}</span> : null}
        <div className={styles.trigger}>
          <button
            type="button"
            className={styles.iconButton}
            data-picker-toggle="true"
            aria-label={label}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => toggleField(field)}
          >
            <CalendarTodayOutlinedIcon className={styles.calendarIcon} />
          </button>
          <input
            className={styles.valueInput}
            value={inputValue}
            inputMode="numeric"
            autoComplete="off"
            maxLength={maxLength}
            placeholder={placeholder}
            aria-label={label}
            onFocus={(event) => {
              setFocusedField(field);
              // The field is always at maxLength, so plain typing would be rejected.
              // Selecting the value lets the first keystroke replace it.
              justFocusedRef.current = true;
              event.target.select();
              // Typing mode: never keep/open the dropdown from input focus.
              if (openField === field) setOpenField(null);
            }}
            onMouseUp={(event) => {
              // Chrome drops the focus-time selection when the click completes.
              if (justFocusedRef.current) {
                event.preventDefault();
                justFocusedRef.current = false;
              }
            }}
            onChange={(event) => onInputChange(digitsOnly(event.target.value, maxLength))}
            onBlur={(event) => {
              setFocusedField(null);
              justFocusedRef.current = false;
              // Read the live DOM value so "26" expands to 2026 and fetches on blur
              // (same as 4-digit auto-commit, without needing Enter).
              const liveValue = digitsOnly(event.target.value, maxLength);
              onCommit(liveValue, { revertIfInvalid: true });
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                const liveValue = digitsOnly(event.currentTarget.value, maxLength);
                onCommit(liveValue, { revertIfInvalid: true });
                event.currentTarget.blur();
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                if (field === 'year') {
                  const reverted = String(selectedYear);
                  setInputYearValue(reverted);
                  event.currentTarget.value = reverted;
                }
                if (field === 'month') {
                  const reverted = String(selectedMonth).padStart(2, '0');
                  setInputMonthValue(reverted);
                  event.currentTarget.value = reverted;
                }
                setOpenField(null);
                event.currentTarget.blur();
              }
            }}
          />
          <button
            type="button"
            className={styles.iconButton}
            data-picker-toggle="true"
            aria-label={label}
            aria-expanded={isOpen}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => toggleField(field)}
          >
            <span className={styles.chevron} aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={rootRef}
      className={`${styles.row} ${direction === 'rtl' ? styles.rtl : styles.ltr}${yearOnly ? ` ${styles.yearOnly}` : ''}`}
    >
      {renderField({
        field: 'year',
        label: labelYear,
        inputValue: inputYearValue,
        onInputChange: handleYearInputChange,
        onCommit: (raw, options) => applyYear(raw ?? inputYearValueRef.current, options),
        maxLength: 4,
        placeholder: 'YYYY',
      })}
      {!yearOnly &&
        renderField({
          field: 'month',
          label: labelMonth,
          inputValue: inputMonthValue,
          onInputChange: handleMonthInputChange,
          onCommit: (raw, options) => applyMonth(raw ?? inputMonthValueRef.current, options),
          maxLength: 2,
          placeholder: 'MM',
        })}
      {list}
    </div>
  );
};

export default FilterMonthYearPicker;
